import type { Event } from '@hvf/contracts'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { rrulestr } from 'rrule'

dayjs.extend(utc)

export type EventOccurrence = Event & {
	localTime: dayjs.Dayjs
	/** index within the current expansion window */
	visibleIndex: number
	/** A flag so the UI can optionally show an "Edited" icon */
	isOverridden?: boolean
}

export type DateRange = {
	start: Date
	end: Date
}

export const getOccurrences = (event: Event, range: DateRange): EventOccurrence[] => {
	const dbStart = dayjs.utc(event.starts_at)

	// 1. Handle Single Events (No RRule)
	if (!event.rrule) {
		// Respect the override even if it's a single event
		const finalStart = event.override_starts_at
			? dayjs.utc(event.override_starts_at)
			: dbStart

		const occ: EventOccurrence = {
			...event,
			localTime: finalStart.local(),
			visibleIndex: 1,
			isOverridden: !!event.override_starts_at,
		}

		// Ensure the event actually falls within the month we are looking at
		const rangeStart = dayjs(range.start)
		const rangeEnd = dayjs(range.end)
		if (occ.localTime.isBefore(rangeStart) || occ.localTime.isAfter(rangeEnd)) {
			return []
		}

		return [occ]
	}

	// 2. Handle Recurring Events
	try {
		const dtStartStr = `DTSTART:${dbStart.format('YYYYMMDDTHHmmss')}Z`
		const rruleString = `${dtStartStr}\n${event.rrule}`
		const rule = rrulestr(rruleString)

		// IMPORTANT: Expand the search window by 7 days.
		// If an event originally scheduled for Feb 28 is overridden to Mar 1,
		// rrule.between(Mar 1, Mar 31) won't generate the Feb 28 instance for us to check!
		const searchStart = dayjs(range.start).subtract(7, 'day').toDate()
		const searchEnd = dayjs(range.end).add(7, 'day').toDate()

		const dates = rule.between(searchStart, searchEnd, true)

		const occurrences = dates
			.map((date): EventOccurrence | null => {
				const originalLocalTime = dayjs.utc(date).local()

				// Format the original date to check against the target override date
				// e.g. "2026-03-17"
				const originalDateStr = originalLocalTime.format('YYYY-MM-DD')

				let finalLocalTime = originalLocalTime
				let isOverridden = false

				// LOGGING BLOCK
				if (
					event.override_target_date &&
					event.override_target_date === originalDateStr
				) {
					console.log(
						`[DateUtils] Match! Target Date ${originalDateStr} found on event: ${event.group}`,
					)

					if (event.override_starts_at) {
						console.log(` -> DB stored UTC Override: ${event.override_starts_at}`)
						finalLocalTime = dayjs.utc(event.override_starts_at).local()
						console.log(
							` -> Converted to Local: ${finalLocalTime.format('YYYY-MM-DD HH:mm:ss')}`,
						)
						isOverridden = true
					}
				}

				// Enforce that occurrences don't happen before the very first event creation time
				const startLocal = dbStart.local()
				if (finalLocalTime.isBefore(startLocal, 'minute')) {
					return null
				}

				// Now that overrides are applied, strictly filter out anything
				// that doesn't belong in the exact requested month range.
				const rangeStart = dayjs(range.start)
				const rangeEnd = dayjs(range.end)
				if (finalLocalTime.isBefore(rangeStart) || finalLocalTime.isAfter(rangeEnd)) {
					return null
				}

				return {
					...event,
					localTime: finalLocalTime,
					visibleIndex: 0, // Placeholder, will set below
					isOverridden,
				}
			})
			.filter((occ): occ is EventOccurrence => occ !== null)

		// Map the final visible index safely based on the filtered results
		return occurrences.map((occ, index) => ({
			...occ,
			visibleIndex: index + 1,
		}))
	} catch (e) {
		console.error(`RRule Error for event ${event.id}:`, e)
		return []
	}
}
